import React from "react";
import { createPortal } from "react-dom";

// The hub never scrolls the window: html/body/#root are overflow:hidden and the
// scroll lives in `.main > .content` (index.css). That element is keyed on the
// route in App.js, so it REMOUNTS on every navigation -- the listener has to
// rebind to the new element each time, which is why this takes the route key.
export const BACK_TO_TOP_THRESHOLD = 360;

// Fixed elements are positioned from the VIEWPORT edge, but the scrollbar lives
// inside the scroll container, so a plain `right: 22px` crowds it (measured on
// the marketing site: a 15px scrollbar left a 7px gap). Publish the real gutter
// as --scroll-gutter. It is re-measured on scroll because async content can
// make the scrollbar appear after mount; overlay scrollbars (macOS, mobile)
// correctly measure 0.
let lastGutter = null;
function syncScrollGutter(el) {
  const gutter = Math.max(0, el.offsetWidth - el.clientWidth);
  if (gutter === lastGutter) return;
  lastGutter = gutter;
  document.documentElement.style.setProperty("--scroll-gutter", `${gutter}px`);
}

function prefersReducedMotion() {
  try {
    return !!window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
  } catch {
    return false;
  }
}

export default function BackToTop({ routeKey }) {
  const [scrollEl, setScrollEl] = React.useState(null);
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    setScrollEl(document.querySelector(".main > .content"));
  }, [routeKey]);

  React.useEffect(() => {
    if (!scrollEl) {
      setVisible(false);
      return undefined;
    }
    let frame = 0;
    const update = () => {
      frame = 0;
      setVisible(scrollEl.scrollTop > BACK_TO_TOP_THRESHOLD);
      syncScrollGutter(scrollEl);
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    scrollEl.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      scrollEl.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [scrollEl]);

  const onClick = () => {
    scrollEl?.scrollTo({
      top: 0,
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
  };

  return createPortal(
    <button
      type="button"
      className={`backToTop${visible ? " isVisible" : ""}`}
      onClick={onClick}
      aria-label="Scroll to top"
      title="Back to top"
      tabIndex={visible ? 0 : -1}
      aria-hidden={visible ? undefined : "true"}
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M6 14.5l6-6 6 6" />
      </svg>
    </button>,
    document.body,
  );
}
